import React, { useContext, useMemo, useState } from 'react';
import Cookies from 'js-cookie';

import {
  CollectionMethodContentfulType,
  ImageType,
  Variant,
} from 'utils/types';
import { ProductInfoContext } from '../../../templates/pdp';
import { UserContext } from '../../../components/Layout';
import { PDPHero } from '../../../components/Hero';
import microscope from './microscope.svg';
import stethoscope from './stethoscope.svg';
import { addToCart, addToCartDataLayer, resetSession } from 'utils/cartHelpers';
import ewAnalytics from 'utils/analytics';
import { useMembershipProducts } from 'utils/hooks';
import { ANALYTICS } from 'utils/constants/analytics';
import Img from 'gatsby-image';

export type SubscriptionOptionType = {
  id: number;
  label: string;
  name: string;
  savingsStatement: string;
  percentDiscount: number;
  price: string;
};

export const ONE_TIME_PURCHASE = 'oneTimePurchase';
export const SUBSCRIBE = 'subscribe';

export const purchaseTypeRadioButtons = [
  { label: 'One time purchase', name: ONE_TIME_PURCHASE },
  { label: 'Subscribe & Save', name: SUBSCRIBE },
];

export type HeroContainerProps = {
  consultAvailable?: boolean;
  consultAvailableText: string;
  descriptionHeader: string;
  descriptionText: string;
  measuresText: string;
  sliderImages: ImageType[];
  modalTitle?: string;
  modalButtonText?: string;
  listOfMeasuredItems?: {
    listOfContentSections: Array<{
      description: {
        description: string;
      };
      title: string;
      image: ImageType;
    }>;
  };
  hideSubscription: any;
  descriptionCallout?: string;
  ctaSubheadline: string;
};

export const HeroContainer: React.FC<HeroContainerProps> = ({
  //@ts-ignore
  consultAvailable,
  consultAvailableText,
  descriptionHeader,
  descriptionText,
  measuresText,
  sliderImages,
  modalButtonText,
  modalTitle,
  listOfMeasuredItems,
  hideSubscription,
  descriptionCallout,
  ctaSubheadline,
}) => {
  const userData = useContext(UserContext);
  const productData = useContext(ProductInfoContext);
  const structuredProductData = {};
  //@ts-ignore
  structuredProductData[productData.slug] = productData;
  const productWithMembership = useMembershipProducts(
    userData,
    structuredProductData,
  );
  const {
    name,
    price,
    memberPrice,
    memberHeroCopy,
    activeCredits,
    reviewData,
    collectionMethods,
    subscriptionVariants = [],
    variantId,
    membershipVariantId,
    productId,
    primaryCallouts,
    outOfStock,
  } = productWithMembership[productData.slug];

  let reviewCount = 0;
  let averageRating = 0;

  if (reviewData) {
    reviewCount = reviewData.review_count;
    averageRating = reviewData.average_rating;
  }
  const experimentCookie = Cookies.get('homepage-experiment');

  const images = sliderImages.map(img => {
    return {
      image: (
        <Img
          fluid={img.fluid}
          loading="eager"
          fadeIn={false}
          style={{ position: 'absolute', width: '100%', height: '100%' }}
          imgStyle={{ objectFit: 'contain' }}
          alt={img.description}
        />
      ),
      imageAlt: img.description,
    };
  });

  const methods = collectionMethods.map(
    (method: CollectionMethodContentfulType) => {
      return {
        name: method.v2Name ? method.v2Name : '',
        iconUrl: method.v2Icon ? method.v2Icon.file.url : '',
      };
    },
  );

  const measurements = [{ name: measuresText, iconUrl: microscope }];

  const marketingCallout =
    primaryCallouts && primaryCallouts.length
      ? primaryCallouts[0].text
      : undefined;

  // Format subscription options
  const subscriptionOptionsByName = useMemo(
    () =>
      subscriptionVariants.reduce(
        (acc: { [key: string]: SubscriptionOptionType }, v: Variant) => {
          const {
            subscription_plan: subscriptionPlan,
            id,
            price: subscriptionPrice,
          } = v;
          const { name, percent_discount: percentDiscount } =
            subscriptionPlan || {};

          const savings = parseInt(price) - parseInt(subscriptionPrice);

          // @ts-ignore
          acc[name] = {
            id,
            price: subscriptionPrice,
            label: `${name} Subscription: $${parseInt(
              subscriptionPrice,
            ).toFixed(0)}`,
            percentDiscount,
            name,
            savingsStatement: `YOU SAVE $${savings.toFixed(
              0,
            )} (${percentDiscount}%)`,
          };
          return acc;
        },
        {},
      ),
    [subscriptionVariants],
  );
  const defaultSubscriptionOption = Object.keys(subscriptionOptionsByName)[2];

  const [activeSubscriptionOption, setActiveSubscriptionOption] = useState<
    SubscriptionOptionType
  >(subscriptionOptionsByName[defaultSubscriptionOption]);

  const handleSubscriptionOptionChange = (
    e: React.FormEvent<HTMLInputElement>,
  ) => {
    const target = e.target as HTMLInputElement;
    setActiveSubscriptionOption(subscriptionOptionsByName[target.name]);
    ewAnalytics.track({
      event: ANALYTICS.EVENTS.CHANGED_FREQUENCY,
      data: { frequency: target.name },
    });
  };
  const [activePurchaseType, setActivePurchaseType] = useState<string>(
    purchaseTypeRadioButtons[0].name,
  );

  const handlePurchaseTypeChange = (e: React.FormEvent<HTMLInputElement>) => {
    const target = e.target as HTMLInputElement;
    setActivePurchaseType(target.name);
    ewAnalytics.track({
      event: ANALYTICS.EVENTS.PURCHASE_TYPE,
      data: { type: target.name, product: name },
    });
  };

  const handleFrequencyTriggerClick = () => {
    ewAnalytics.track({
      event: ANALYTICS.EVENTS.CHANGE_FREQUENCY_CLICKED,
      data: { product: name },
    });
  };

  const [quantity, setQuantity] = useState<number>(1);

  const handleExpressPayClick = (cb: any) => {
    resetSession();
    if (activePurchaseType === SUBSCRIBE) {
      addToCart(activeSubscriptionOption.id, undefined, cb, 1);
      addToCartDataLayer(activeSubscriptionOption.id, name, price);
    } else {
      addToCart(variantId, undefined, cb, quantity);
      addToCartDataLayer(variantId, name, price);
    }
  };

  const handleAddToCartClick = (e: React.FormEvent<HTMLButtonElement>) => {
    const target = e.target as HTMLInputElement;
    const label =
      target.dataset.name === ANALYTICS.LABELS.ADD_TO_CART_MODAL
        ? ANALYTICS.LABELS.ADD_TO_CART_MODAL
        : ANALYTICS.LABELS.ADD_TO_CART;
    ewAnalytics.track({
      data: {
        label,
        'Test ID': productId,
        'Test Name': name,
        Experiment: experimentCookie ? experimentCookie : '',
      },
      event: ANALYTICS.EVENTS.ADD_TO_CART,
    });
    if (activePurchaseType === SUBSCRIBE) {
      addToCart(activeSubscriptionOption.id, undefined, undefined, 1);
      addToCartDataLayer(activeSubscriptionOption.id, name, price);
    } else {
      addToCart(variantId, undefined, undefined, quantity);
      addToCartDataLayer(variantId, name, price);
    }
  };

  const handleQuantityPickerChange = (e: React.FormEvent<HTMLInputElement>) => {
    const target = e.target as HTMLInputElement;
    setQuantity(+target.value);
    ewAnalytics.track({
      event: ANALYTICS.EVENTS.QUANTITY_SELECT,
      data: {
        name,
        productId,
        quantity: target.value,
      },
    });
  };

  const handleSlide = () => {
    ewAnalytics.track({
      event: ANALYTICS.EVENTS.HERO_SLIDE,
      data: { name, productId },
    });
  };

  const oneTimePurchaseOnlyRadioButtons = [purchaseTypeRadioButtons[0]];

  const formattedMeasuredItems =
    listOfMeasuredItems?.listOfContentSections?.map(item => {
      const formattedItemNames = item.description?.description.split('\n');
      return {
        image: item.image?.file?.url || '',
        title: item.title,
        items: formattedItemNames,
      };
    }) || [];

  return (
    <PDPHero
      name={name}
      descriptionHeader={descriptionHeader}
      description={descriptionText}
      price={price}
      memberPrice={memberPrice}
      memberHeroCopy={memberHeroCopy}
      membershipVariantId={membershipVariantId}
      activeCredits={activeCredits}
      averageRating={averageRating}
      reviewCount={reviewCount}
      sliderImages={[...images]}
      collectionMethods={methods}
      consultAvailable={consultAvailable}
      consultAvailableText={consultAvailableText}
      consultIcon={stethoscope}
      measurements={measurements}
      subscriptionOptions={Object.values(subscriptionOptionsByName)}
      onSubscriptionOptionChange={handleSubscriptionOptionChange}
      activeSubscriptionOption={activeSubscriptionOption}
      activePurchaseType={activePurchaseType}
      purchaseTypeRadioButtons={
        subscriptionVariants.length
          ? purchaseTypeRadioButtons
          : oneTimePurchaseOnlyRadioButtons
      }
      onPurchaseTypeChange={handlePurchaseTypeChange}
      onAddToCartClick={handleAddToCartClick}
      onExpressPayClick={handleExpressPayClick}
      subscribeSelected={activePurchaseType === SUBSCRIBE}
      onQuantityPickerChange={handleQuantityPickerChange}
      quantity={quantity}
      topCallout={undefined}
      bottomCallout={marketingCallout}
      descriptionCallout={descriptionCallout}
      modalButtonText={modalButtonText}
      modalTitle={modalTitle}
      listOfMeasuredItems={formattedMeasuredItems}
      hideSubscription={!!hideSubscription}
      onFrequencyTriggerClick={handleFrequencyTriggerClick}
      onSlide={handleSlide}
      ctaSubheadline={ctaSubheadline}
      isOutOfStock={outOfStock}
    />
  );
};

export default HeroContainer;
